import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { message, notification } from 'antd';
import dayjs from 'dayjs';

import {
  useCreatePatientDossier,
  useUpdatePatientDossier,
  type PatientDossierPayload,
  type PatientDossierPriorDelivery,
  type PatientDossierResponseData,
  type PatientDossierSectionError,
  type PatientDossierUpdatePayload,
} from '../../../../helpers/api/patients/patients';
import type { Patient } from '../../../interfaces/patient';
import { PartialErrorsBanner } from './components/PartialErrorsBanner';
import { TabNav, type TabDescriptor } from './components/TabNav';
import {
  emptyPriorDelivery,
  EMPTY_FAMILY,
  EMPTY_OTHER_FAMILY,
  INITIAL_CORE,
  INITIAL_DISABILITY,
  INITIAL_HISTORY_LISTS,
  INITIAL_PREVENTIVE_CARE,
  INITIAL_PREGNANCY,
  newOtherFamilyEntry,
  type PredefinedFamilyConditionCode,
} from './constants';
import { AccouchementTab } from './tabs/AccouchementTab';
import { AntecedentTab } from './tabs/AntecedentTab';
import { CoreTab } from './tabs/CoreTab';
import { GrossesseTab } from './tabs/GrossesseTab';
import type {
  CoreData,
  CurrentPregnancyData,
  DicomPatientProfile,
  DisabilityCode,
  DisabilityState,
  EducationLevel,
  FamilyConditionState,
  FamilyMemberCode,
  HistoryListState,
  HistorySection,
  OtherFamilyEntry,
  OtherFamilyState,
  PreventiveCareData,
  PriorDeliveryRowState,
  ServerValidationError,
  TabId,
} from './types';
import {
  buildFamilyHistory,
  buildPatientMedicalHistory,
  buildPatientPayload,
  buildPregnancy,
  buildPriorDeliveries,
} from './utils/buildPayload';
import { tabForFieldError, tabForSection } from './utils/errorMapping';
import { validateDossierForm } from './utils/validate';
import {
  hydrateDisability,
  hydrateFamilyState,
  hydrateHistoryLists,
  hydratePreventiveCare,
  hydratePregnancy,
  hydratePriorDeliveries,
  type DossierForEdit,
  type PriorDeliveryHydrated,
} from './utils/hydrateFromDossier';
import {
  buildDossierSectionSummary,
  type DossierSectionSummary,
} from './utils/dossierSectionSummary';
import type { PacsStudy } from '../../NewCaseWizard/PacsSyncWizard';

interface AddPatientFormProps {
  patientToEdit?: Patient | null;
  dossierForEdit?: DossierForEdit | null;
  onPatientCreated?: (newPatient: Patient) => void;
  onPatientUpdated?: () => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  selectedPacsStudy?: PacsStudy;
  initialDataFromDicom?: DicomPatientProfile;
}

const PARTIAL_DOSSIER_NOTIFICATION_KEY = 'patient-dossier-partial-save';

export const AddPatientForm = forwardRef<HTMLFormElement, AddPatientFormProps>(
  (
    {
      patientToEdit,
      dossierForEdit,
      onPatientCreated,
      onPatientUpdated,
      onSubmittingChange,
      selectedPacsStudy,
      initialDataFromDicom,
    },
    ref
  ) => {
    const { t } = useTranslation('Patient');
    const queryClient = useQueryClient();
    const isEditMode = !!patientToEdit;

    const [activeTab, setActiveTab] = useState<TabId>('core');
    const [core, setCore] = useState<CoreData>(INITIAL_CORE);
    const [family, setFamily] =
      useState<Record<PredefinedFamilyConditionCode, FamilyConditionState>>(
        EMPTY_FAMILY
      );
    const [otherFamily, setOtherFamily] =
      useState<OtherFamilyState>(EMPTY_OTHER_FAMILY);
    const [historyLists, setHistoryLists] = useState<
      Record<HistorySection, HistoryListState>
    >(INITIAL_HISTORY_LISTS);
    const [disability, setDisability] =
      useState<DisabilityState>(INITIAL_DISABILITY);
    const [preventiveCare, setPreventiveCare] =
      useState<PreventiveCareData>(INITIAL_PREVENTIVE_CARE);
    const [priorDeliveries, setPriorDeliveries] = useState<
      PriorDeliveryHydrated[]
    >([]);
    const [pregnancy, setPregnancy] =
      useState<CurrentPregnancyData>(INITIAL_PREGNANCY);

    const lastInitializedId = useRef<string | null>(null);

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [ageInputError, setAgeInputError] = useState('');
    const [pregnancyInputErrors, setPregnancyInputErrors] = useState<
      Record<string, string>
    >({});
    const [sectionErrors, setSectionErrors] = useState<
      PatientDossierSectionError[]
    >([]);
    const [sectionSummary, setSectionSummary] =
      useState<DossierSectionSummary | null>(null);
    const displayErrors = useMemo(() => {
      const next = { ...errors };
      if (ageInputError) {
        next.age_years = ageInputError;
      }
      Object.entries(pregnancyInputErrors).forEach(([field, error]) => {
        if (error) {
          next[field] = error;
        }
      });
      return next;
    }, [ageInputError, errors, pregnancyInputErrors]);

    const setPregnancyInputError = <K extends keyof CurrentPregnancyData>(
      key: K,
      error: string
    ) => {
      setPregnancyInputErrors((prev) => {
        const current = prev[key as string];
        if (current === error) {
          return prev;
        }
        const next = { ...prev };
        if (error) {
          next[key as string] = error;
        } else {
          delete next[key as string];
        }
        return next;
      });
    };

    const translateErrorMessage = (msg: string): string => {
      if (!msg) {
        return '';
      }

      const maxCharMatch = msg.match(/at most (\d+)/);
      if (maxCharMatch) {
        return t('maxLengthError', {
          max: maxCharMatch[1],
          defaultValue: msg,
        });
      }

      const minMatch = msg.match(/greater than or equal to (-?\d+)/);
      if (minMatch) {
        return t('outOfRange', { min: minMatch[1], defaultValue: msg });
      }

      const maxMatch = msg.match(/less than or equal to (\d+)/);
      if (maxMatch) {
        return t('outOfRangeMax', { max: maxMatch[1], defaultValue: msg });
      }

      if (msg.includes('Field required')) {
        return t('fieldRequired');
      }
      if (msg.includes('valid integer') || msg.includes('valid number')) {
        return t('numericOnly');
      }
      if (msg.includes('Age must be a whole number')) {
        return t('ageRangeError');
      }

      return t(msg, { defaultValue: msg });
    };

    const getFieldLabel = (key: string): string => {
      const fieldLabelMap: Record<string, string> = {
        patient_last_name: t('lastName'),
        patient_name: t('firstName'),
        phone_number: t('dossier_phone'),
        email: t('emailAddress'),
        cin: t('cin'),
        birth_date: t('dateOfBirth'),
        age_years: t('dossier_age_label'),
        address: t('address'),
        occupation: t('occupation'),
        education_level: t('educationLevel'),
        marital_status: t('dossier_marital'),
        last_menstrual_period: t('dossier_lmp'),
        estimated_due_date: t('dossier_edd'),
        grossesse_classee_a_risque: t('dossier_pregnancy_high_risk'),
        risque_autre_details: t('dossier_pregnancy_risk_other'),
        ga_weeks: t('dossier_weeks'),
        ga_days: t('dossier_days'),
        preg_crl: t('dossier_crl'),
        preg_bpd: t('dossier_bpd'),
        preg_hc: t('dossier_hc'),
        preg_ac: t('dossier_ac'),
        preg_fl: t('dossier_fl'),
      };
      if (key.startsWith('prior_delivery_')) {
        const parts = key.split('_');
        const rowNum = Number(parts[2]) + 1;
        return `${t('dossier_delivery_number')} ${rowNum}`;
      }
      return fieldLabelMap[key] || key;
    };

    const handleAgeValidationChange = (rawError: string) => {
      setAgeInputError(translateErrorMessage(rawError));
    };

    const handlePregnancyValidationChange = <
      K extends keyof CurrentPregnancyData,
    >(
      key: K,
      rawError: string
    ) => {
      setPregnancyInputError(key, translateErrorMessage(rawError));
    };

    useEffect(() => {
      const currentId = patientToEdit?.id ? String(patientToEdit.id) : 'new';
      if (lastInitializedId.current === currentId) {
        return;
      }

      setAgeInputError('');
      setPregnancyInputErrors({});
      if (isEditMode && patientToEdit) {
        const extras = patientToEdit as unknown as Record<string, unknown>;
        const birthDateStr = patientToEdit.birth_date
          ? dayjs(patientToEdit.birth_date).format('YYYY-MM-DD')
          : '';

        const ageFromExtras =
          typeof extras.age_years === 'number' ? extras.age_years : null;
        const derivedAge =
          ageFromExtras != null
            ? ageFromExtras
            : birthDateStr
              ? Math.max(0, dayjs().diff(dayjs(birthDateStr), 'year'))
              : null;

        const pickString = (key: string): string => {
          const v = extras[key];
          return typeof v === 'string' ? v : '';
        };
        const pickBool = (key: string): boolean | null => {
          const v = extras[key];
          return typeof v === 'boolean' ? v : null;
        };
        const pickNumber = (key: string): number | null => {
          const v = extras[key];
          if (typeof v === 'number' && Number.isFinite(v)) {
            return v;
          }
          if (typeof v === 'string' && v.trim() !== '') {
            const n = Number(v);
            return Number.isFinite(n) ? n : null;
          }
          return null;
        };
        const pickEnum = <T extends string>(
          key: string,
          allowed: readonly T[]
        ): T | '' => {
          const v = extras[key];
          return typeof v === 'string' &&
            (allowed as readonly string[]).includes(v)
            ? (v as T)
            : '';
        };

        setCore({
          ...INITIAL_CORE,
          patient_last_name: patientToEdit.patient_last_name || '',
          patient_name: patientToEdit.patient_name || '',
          patient_middle_name: patientToEdit.patient_middle_name || '',
          birth_date: birthDateStr,
          age_years: derivedAge,
          cin: patientToEdit.cin || '',
          address: patientToEdit.address || '',
          education_level:
            (patientToEdit.education_level as EducationLevel) || '',
          occupation: patientToEdit.occupation || '',
          phone_number: patientToEdit.phone_number || '',
          email: patientToEdit.email || '',
          reminders_opt_out: !!patientToEdit.reminders_opt_out,
          consanguinity:
            typeof patientToEdit.consanguinity === 'boolean'
              ? patientToEdit.consanguinity
              : pickBool('consanguinity'),
          coverage_mode: (pickString('coverage_mode') ||
            '') as CoreData['coverage_mode'],
          medical_coverage_label: pickEnum('medical_coverage_label', [
            'CNSS',
            'CNOPS',
            'FAR',
            'RAMED',
            'MUTUELLE',
            'NON_INSURED',
          ] as const),
          woman_origin: (pickString('woman_origin') ||
            '') as CoreData['woman_origin'],
          marital_status: (pickString('marital_status') ||
            '') as CoreData['marital_status'],
          spouse_name: pickString('spouse_name'),
          spouse_occupation: pickString('spouse_occupation'),
          habit_caffeine: pickBool('habit_caffeine'),
          habit_alcohol: pickBool('habit_alcohol'),
          habit_tobacco: pickBool('habit_tobacco'),
          habit_medications: pickBool('habit_medications'),
          habit_geophagy: pickBool('habit_geophagy'),
          habit_other_notes: pickString('habit_other_notes'),
          blood_group_abo: pickEnum('blood_group_abo', [
            'A',
            'B',
            'O',
            'AB',
          ] as const),
          blood_group_rhesus: pickEnum('blood_group_rhesus', [
            'POSITIVE',
            'NEGATIVE',
          ] as const),
          husband_blood_group_abo: pickEnum('husband_blood_group_abo', [
            'A',
            'B',
            'O',
            'AB',
          ] as const),
          husband_blood_group_rhesus: pickEnum('husband_blood_group_rhesus', [
            'POSITIVE',
            'NEGATIVE',
          ] as const),
          weight_kg: pickNumber('weight_kg'),
          height_cm: pickNumber('height_cm'),
        });

        if (dossierForEdit) {
          const { family: hydratedFamily, otherFamily: hydratedOther } =
            hydrateFamilyState(dossierForEdit.family_history ?? []);
          setFamily(hydratedFamily);
          setOtherFamily(hydratedOther);
          setHistoryLists(
            hydrateHistoryLists(dossierForEdit.patient_medical_history)
          );
          setDisability(
            hydrateDisability(dossierForEdit.patient_medical_history)
          );
          setPreventiveCare(
            hydratePreventiveCare(dossierForEdit.patient_medical_history)
          );
          setPriorDeliveries(
            hydratePriorDeliveries(dossierForEdit.prior_deliveries ?? [])
          );
          setPregnancy(hydratePregnancy(dossierForEdit.pregnancy));
        }
        lastInitializedId.current = currentId;
        return;
      }

      const next = { ...INITIAL_CORE };
      if (initialDataFromDicom) {
        next.patient_name = initialDataFromDicom.firstName || '';
        next.patient_last_name = initialDataFromDicom.lastName || '';
        const dob = initialDataFromDicom.patientBirthDate;
        if (dob && dob.length === 8) {
          const formatted = dayjs(dob, 'YYYYMMDD').format('YYYY-MM-DD');
          if (dayjs(formatted).isValid()) {
            next.birth_date = formatted;
            next.age_years = Math.max(
              0,
              dayjs().diff(dayjs(formatted), 'year')
            );
          }
        }
      } else if (selectedPacsStudy?.patientName) {
        const parts = selectedPacsStudy.patientName.split('^');
        next.patient_last_name = parts[0]?.trim() || '';
        next.patient_name = parts[1]?.trim() || '';
      }
      setCore(next);
      lastInitializedId.current = currentId;
    }, [
      isEditMode,
      patientToEdit,
      dossierForEdit,
      initialDataFromDicom,
      selectedPacsStudy,
    ]);

    const updateCore = <K extends keyof CoreData>(
      key: K,
      value: CoreData[K]
    ) => {
      setCore((prev) => ({ ...prev, [key]: value }));
      if (errors[key as string]) {
        setErrors((e) => ({ ...e, [key as string]: '' }));
      }
    };

    const toggleFamily = (code: PredefinedFamilyConditionCode) =>
      setFamily((prev) => {
        const wasActive = prev[code].active;
        return {
          ...prev,
          [code]: wasActive
            ? {
                active: false,
                wife_members: [],
                husband_members: [],
              }
            : {
                ...prev[code],
                active: true,
              },
        };
      });

    const updateFamilyMembers = (
      code: PredefinedFamilyConditionCode,
      side: 'wife_members' | 'husband_members',
      next: FamilyMemberCode[]
    ) =>
      setFamily((prev) => ({
        ...prev,
        [code]: {
          ...prev[code],
          [side]: next,
          active: prev[code].active || next.length > 0,
        },
      }));

    const toggleOtherFamily = () =>
      setOtherFamily((prev) => {
        const willActivate = !prev.active;
        return {
          active: willActivate,
          entries: willActivate
            ? prev.entries.length === 0
              ? [newOtherFamilyEntry()]
              : prev.entries
            : [],
        };
      });

    const addOtherFamilyEntry = () =>
      setOtherFamily((prev) => ({
        active: true,
        entries: [...prev.entries, newOtherFamilyEntry()],
      }));

    const removeOtherFamilyEntry = (id: string) =>
      setOtherFamily((prev) => ({
        ...prev,
        entries: prev.entries.filter((e) => e.id !== id),
      }));

    const updateOtherFamilyEntry = (
      id: string,
      patch: Partial<OtherFamilyEntry>
    ) =>
      setOtherFamily((prev) => ({
        ...prev,
        entries: prev.entries.map((e) =>
          e.id === id ? { ...e, ...patch } : e
        ),
      }));

    const toggleHistoryList = (section: HistorySection) =>
      setHistoryLists((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          active: !prev[section].active,
          items:
            !prev[section].active && prev[section].items.length === 0
              ? ['']
              : prev[section].items,
        },
      }));

    const updateHistoryItem = (
      section: HistorySection,
      index: number,
      value: string
    ) =>
      setHistoryLists((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          items: prev[section].items.map((it, i) => (i === index ? value : it)),
        },
      }));

    const addHistoryItem = (section: HistorySection) =>
      setHistoryLists((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          items: [...prev[section].items, ''],
        },
      }));

    const removeHistoryItem = (section: HistorySection, index: number) =>
      setHistoryLists((prev) => {
        const remaining = prev[section].items.filter((_, i) => i !== index);
        return {
          ...prev,
          [section]: {
            ...prev[section],
            items: remaining.length ? remaining : [''],
          },
        };
      });

    const toggleDisabilityType = (value: DisabilityCode) =>
      setDisability((prev) => {
        const has = prev.types.includes(value);
        const nextTypes = has
          ? prev.types.filter((v) => v !== value)
          : [...prev.types, value];
        return {
          types: nextTypes,
          other_description: nextTypes.includes('OTHER')
            ? prev.other_description
            : '',
        };
      });

    const clearDisability = () => setDisability(INITIAL_DISABILITY);

    const updateDisabilityOther = (value: string) =>
      setDisability((prev) => ({ ...prev, other_description: value }));

    const updatePreventiveCare = <K extends keyof PreventiveCareData>(
      key: K,
      value: PreventiveCareData[K]
    ) =>
      setPreventiveCare((prev) => {
        const next = { ...prev, [key]: value };
        if (key === 'rubella_vaccinated' && value !== true) {
          next.rubella_vaccination_date = '';
        }
        if (key === 'hepatitis_b_vaccinated' && value !== true) {
          next.hepatitis_b_vaccination_date = '';
        }
        return next;
      });

    const addPriorDelivery = (withAnomaly = false) =>
      setPriorDeliveries((prev) => [...prev, emptyPriorDelivery(withAnomaly)]);
    const removePriorDelivery = (idx: number) =>
      setPriorDeliveries((prev) => prev.filter((_, i) => i !== idx));
    const clearPriorDeliveryFieldError = (
      idx: number,
      key: keyof PriorDeliveryRowState
    ) => {
      const errorKey = `prior_delivery_${idx}_${String(key)}`;
      if (errors[errorKey]) {
        setErrors((prev) => ({ ...prev, [errorKey]: '' }));
      }
    };
    const updatePriorDelivery = <K extends keyof PriorDeliveryRowState>(
      idx: number,
      key: K,
      value: PriorDeliveryRowState[K]
    ) => {
      clearPriorDeliveryFieldError(idx, key);
      setPriorDeliveries((prev) =>
        prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row))
      );
    };

    const togglePriorDeliveryMode = (idx: number, withAnomaly: boolean) =>
      setPriorDeliveries((prev) =>
        prev.map((row, i) =>
          i === idx ? { ...row, has_obstetric_anomalies: withAnomaly } : row
        )
      );

    const clearAnomalyError = (idx: number) => {
      const key = `prior_delivery_${idx}_anomaly_type`;
      if (errors[key]) {
        setErrors((prev) => ({ ...prev, [key]: '' }));
      }
    };

    const updatePregnancy = <K extends keyof CurrentPregnancyData>(
      key: K,
      value: CurrentPregnancyData[K]
    ) => {
      setPregnancy((prev) => ({ ...prev, [key]: value }));
      setPregnancyInputError(key, '');
      if (errors[key as string]) {
        setErrors((prev) => ({ ...prev, [key as string]: '' }));
      }
    };

    const patchPregnancy = (patch: Partial<CurrentPregnancyData>) => {
      setPregnancy((prev) => ({ ...prev, ...patch }));
      Object.keys(patch).forEach((key) => {
        setPregnancyInputError(key as keyof CurrentPregnancyData, '');
      });
      const keysToClear = Object.keys(patch).filter((key) => errors[key]);
      if (keysToClear.length > 0) {
        setErrors((prev) => {
          const next = { ...prev };
          keysToClear.forEach((key) => {
            next[key] = '';
          });
          return next;
        });
      }
    };

    const applyServerFieldErrors = (next: Record<string, string>) => {
      if (Object.keys(next).length === 0) {
        return false;
      }
      setSectionErrors([]);
      const translated: Record<string, string> = {};
      Object.entries(next).forEach(([key, value]) => {
        translated[key] = translateErrorMessage(value);
      });
      setErrors(translated);
      const firstField = Object.keys(translated)[0];
      const label = getFieldLabel(firstField);
      const translatedMsg = translated[firstField];
      setActiveTab(tabForFieldError(firstField));
      message.error(
        t('fieldErrorWithLabel', {
          label,
          message: translatedMsg,
          defaultValue: `Error [${label}]: ${translatedMsg}`,
        }),
        5
      );
      return true;
    };

    const parseValidationMessage = (rawMessage?: string) => {
      if (!rawMessage) {
        return {};
      }

      const lines = rawMessage.split('\n');
      const next: Record<string, string> = {};

      for (let index = 0; index < lines.length; index += 1) {
        const currentLine = lines[index]?.trim();
        if (
          !currentLine ||
          /^\d+ validation errors? for /i.test(currentLine) ||
          currentLine.startsWith('For further information')
        ) {
          continue;
        }

        const nextLine = lines[index + 1]?.trim();
        if (!nextLine || nextLine.startsWith('For further information')) {
          continue;
        }

        const cleanedMessage = nextLine
          .replace(/^Value error,\s*/i, '')
          .replace(/\s*\[type=.*$/, '')
          .trim();

        if (cleanedMessage) {
          next[currentLine] = translateErrorMessage(cleanedMessage);
        }
      }

      return next;
    };

    const mapServerErrorLocToFieldKey = (loc: (string | number)[]) => {
      const locParts = loc.map((part) => String(part));
      const bodyIndex = locParts.indexOf('body');
      const relevant =
        bodyIndex >= 0 ? locParts.slice(bodyIndex + 1) : [...locParts];

      if (relevant[0] === 'patient' || relevant[0] === 'pregnancy') {
        return relevant[relevant.length - 1] ?? null;
      }

      if (relevant[0] === 'prior_deliveries') {
        const rowIndex = relevant[1];
        const field = relevant[2];
        if (rowIndex != null && field) {
          return `prior_delivery_${rowIndex}_${field}`;
        }
      }

      return relevant[relevant.length - 1] ?? null;
    };

    const handleApiError = (error: unknown) => {
      console.error('Dossier API error', error);
      setSectionSummary(null);
      const errObj = (error ?? {}) as {
        response?: {
          data?: {
            detail?: ServerValidationError[] | string;
            message?: string;
            details?: Record<string, unknown>;
          };
        };
        detail?: ServerValidationError[] | string;
        message?: string;
        details?: Record<string, unknown>;
      };
      const payload = errObj.response?.data ?? errObj;
      const detail = payload.detail;

      if (Array.isArray(detail)) {
        const next: Record<string, string> = {};
        detail.forEach((e: ServerValidationError) => {
          if (e.loc && e.loc.length > 0) {
            const field = mapServerErrorLocToFieldKey(e.loc);
            if (field) {
              next[field] = translateErrorMessage(
                e.msg.replace(/^Value error,\s*/i, '')
              );
            }
          }
        });
        if (applyServerFieldErrors(next)) {
          return;
        }
      }

      const parsedMessageErrors = parseValidationMessage(payload.message);
      if (applyServerFieldErrors(parsedMessageErrors)) {
        return;
      }

      message.error(payload.message || t('unexpectedError'));
    };

    const openPartialDossierNotification = (
      summary: DossierSectionSummary,
      mode: 'create' | 'update'
    ) => {
      notification.warning({
        key: PARTIAL_DOSSIER_NOTIFICATION_KEY,
        message:
          mode === 'create'
            ? t('dossier_partial_create_title')
            : t('dossier_partial_update_title'),
        description: (
          <div className="space-y-2 text-sm">
            <p className="m-0">
              {mode === 'create'
                ? t('dossier_partial_create_description')
                : t('dossier_partial_update_description')}
            </p>
            {summary.saved.length > 0 && (
              <div>
                <span className="font-semibold">
                  {t('dossier_partial_saved_label')}:
                </span>{' '}
                {summary.saved.map((section) => section.label).join(', ')}
              </div>
            )}
            <div>
              <div className="font-semibold">
                {t('dossier_partial_failed_label')}:
              </div>
              <ul className="m-0 mt-1 list-disc pl-5">
                {summary.failed.map((section) => (
                  <li
                    key={`${section.section}-${section.message ?? section.label}`}
                  >
                    <span className="font-medium">{section.label}</span>
                    {section.message ? `: ${section.message}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ),
        duration: 0,
        placement: 'topRight',
      });
    };

    const handleCreateSuccess = (
      response: unknown,
      submittedData: PatientDossierPayload
    ) => {
      const outer = (response ?? {}) as { data?: { data?: unknown } | unknown };
      const inner = (outer.data ?? {}) as { data?: unknown };
      const body = (inner as { data?: unknown }).data ?? outer.data ?? outer;
      const dossier = body as PatientDossierResponseData | undefined;
      const createdPatient = dossier?.patient as unknown as Patient | undefined;
      const summary = buildDossierSectionSummary({
        submittedData,
        responseData: dossier,
        t,
      });

      queryClient.refetchQueries({ queryKey: ['paginatedPatient'] });
      queryClient.refetchQueries({ queryKey: ['analytics'] });

      const errs = dossier?.errors ?? [];
      setSectionErrors(errs);
      setSectionSummary(errs.length > 0 ? summary : null);
      if (errs.length > 0) {
        message.warning(t('dossier_partial_warning', { count: errs.length }));
        openPartialDossierNotification(summary, 'create');
        const firstTab = tabForSection(errs[0].section);
        if (firstTab) {
          setActiveTab(firstTab);
        }
        return;
      }

      notification.destroy(PARTIAL_DOSSIER_NOTIFICATION_KEY);
      message.success(t('patientCreatedSuccess'));
      if (createdPatient) {
        onPatientCreated?.(createdPatient);
      }
    };

    const handleUpdateSuccess = (
      response?: unknown,
      submittedData?: PatientDossierUpdatePayload
    ) => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      if (patientToEdit) {
        queryClient.invalidateQueries({
          queryKey: ['patients', patientToEdit.id],
        });
      }

      const outer = (response ?? {}) as { data?: { data?: unknown } };
      const inner = (outer.data ?? {}) as { data?: unknown; errors?: unknown };
      const body = (inner as { data?: unknown }).data ?? inner;
      const dossier = body as PatientDossierResponseData | undefined;
      const errs = (dossier?.errors ?? []) as PatientDossierSectionError[];
      const summary = buildDossierSectionSummary({
        submittedData: submittedData ?? null,
        responseData: dossier,
        t,
      });

      if (errs.length > 0) {
        const nextFieldErrors: Record<string, string> = {};
        errs.forEach((err) => {
          if (err.section === 'patient') {
            nextFieldErrors.patient_last_name = translateErrorMessage(
              err.message
            );
          }
        });
        setErrors((prev) => ({ ...prev, ...nextFieldErrors }));
        setSectionErrors(errs);
        setSectionSummary(summary);
        message.warning(t('dossier_partial_warning', { count: errs.length }));
        openPartialDossierNotification(summary, 'update');
        if (errs[0]) {
          const firstTab = tabForSection(errs[0].section);
          if (firstTab) {
            setActiveTab(firstTab);
          }
        }
        return;
      }

      setSectionErrors([]);
      setSectionSummary(null);
      notification.destroy(PARTIAL_DOSSIER_NOTIFICATION_KEY);
      message.success(t('patientUpdatedSuccess'));
      onPatientUpdated?.();
    };

    const { mutate: createDossier, isPending: isCreating } =
      useCreatePatientDossier(handleCreateSuccess, handleApiError, false);
    const { mutate: updateDossier, isPending: isUpdating } =
      useUpdatePatientDossier(
        (response, data) => handleUpdateSuccess(response, data),
        (_response, error) => handleApiError(error),
        false
      );

    const isSubmitting = isCreating || isUpdating;
    useEffect(() => {
      onSubmittingChange?.(isSubmitting);
    }, [isSubmitting, onSubmittingChange]);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (isSubmitting) {
        return;
      }

      const result = validateDossierForm(core, priorDeliveries, pregnancy, t);
      const nextErrors = { ...result.errors };
      if (ageInputError) {
        nextErrors.age_years = ageInputError;
      }
      Object.entries(pregnancyInputErrors).forEach(([field, error]) => {
        if (error) {
          nextErrors[field] = error;
        }
      });
      setErrors(nextErrors);
      const firstPregnancyInputError = Object.keys(pregnancyInputErrors).find(
        (field) => Boolean(pregnancyInputErrors[field])
      );
      const firstErrorTab =
        result.firstErrorTab ||
        (ageInputError
          ? tabForFieldError('age_years')
          : firstPregnancyInputError
            ? tabForFieldError(firstPregnancyInputError)
            : null);
      if (firstErrorTab) {
        setActiveTab(firstErrorTab);
      }
      if (Object.keys(nextErrors).length > 0) {
        const firstKey = Object.keys(nextErrors)[0];
        message.error(
          t('fieldErrorWithLabel', {
            label: getFieldLabel(firstKey),
            message: nextErrors[firstKey],
            defaultValue: `Error [${getFieldLabel(firstKey)}]: ${nextErrors[firstKey]}`,
          }),
          5
        );
        return;
      }

      if (isEditMode && patientToEdit) {
        const updateBody: Record<string, unknown> = {
          patient: buildPatientPayload(core),
        };

        const pregnancyPayload = buildPregnancy(pregnancy);
        if (pregnancyPayload) {
          updateBody.pregnancy = pregnancyPayload;
          const existingPregnancyId = (
            dossierForEdit?.pregnancy as { id?: number | string } | null
          )?.id;
          if (existingPregnancyId != null) {
            updateBody.pregnancy_id = existingPregnancyId;
          }
        }

        updateBody.family_history = buildFamilyHistory(family, otherFamily);

        const builtPriorDeliveries = buildPriorDeliveries(priorDeliveries);
        const priorDeliveriesUpsert: PatientDossierPriorDelivery[] =
          builtPriorDeliveries.map((row, idx) => {
            const id = priorDeliveries[idx]?.id ?? null;
            return id != null ? { ...row, id } : row;
          });
        updateBody.prior_deliveries = priorDeliveriesUpsert;

        const pmh = buildPatientMedicalHistory(
          historyLists,
          disability,
          preventiveCare
        );
        if (pmh) {
          updateBody.patient_medical_history = {
            vat_dose: preventiveCare.vat_dose,
            rubella_vaccinated: preventiveCare.rubella_vaccinated,
            rubella_vaccination_date:
              preventiveCare.rubella_vaccinated &&
              preventiveCare.rubella_vaccination_date
                ? preventiveCare.rubella_vaccination_date
                : null,
            hepatitis_b_vaccinated: preventiveCare.hepatitis_b_vaccinated,
            hepatitis_b_vaccination_date:
              preventiveCare.hepatitis_b_vaccinated &&
              preventiveCare.hepatitis_b_vaccination_date
                ? preventiveCare.hepatitis_b_vaccination_date
                : null,
            cervical_smear_iva_date:
              preventiveCare.cervical_smear_iva_date || null,
            ...pmh,
          };
        } else if (dossierForEdit?.patient_medical_history) {
          updateBody.patient_medical_history = {
            medical_hx: null,
            surgical_hx: null,
            gynecological_hx: null,
            disability_type: null,
            disability_other_description: null,
            vat_dose: null,
            rubella_vaccinated: null,
            rubella_vaccination_date: null,
            hepatitis_b_vaccinated: null,
            hepatitis_b_vaccination_date: null,
            cervical_smear_iva_date: null,
          };
        }

        updateDossier({
          patient_id: patientToEdit.id,
          ...updateBody,
        });
        return;
      }

      const payload: PatientDossierPayload = {
        patient: buildPatientPayload(core),
      };
      const pregnancyPayload = buildPregnancy(pregnancy);
      if (pregnancyPayload) {
        payload.pregnancy = pregnancyPayload;
      }
      const fh = buildFamilyHistory(family, otherFamily);
      if (fh.length) {
        payload.family_history = fh;
      }
      const pd = buildPriorDeliveries(priorDeliveries);
      if (pd.length) {
        payload.prior_deliveries = pd;
      }
      const pmh = buildPatientMedicalHistory(
        historyLists,
        disability,
        preventiveCare
      );
      if (pmh) {
        payload.patient_medical_history = pmh;
      }

      createDossier(payload);
    };

    // ------------------------------- tabs -------------------------------
    const tabs: TabDescriptor[] = useMemo(() => {
      const tabsWithErrors = new Set<TabId>();
      for (const key of Object.keys(displayErrors)) {
        if (displayErrors[key]) {
          tabsWithErrors.add(tabForFieldError(key));
        }
      }
      return [
        {
          id: 'core',
          label: t('dossier_tab_core'),
          icon: '📋',
          hasError: tabsWithErrors.has('core'),
        },
        {
          id: 'antecedent',
          label: t('dossier_tab_antecedent'),
          icon: '🕒',
          hasError: tabsWithErrors.has('antecedent'),
        },
        {
          id: 'accouchement',
          label: t('dossier_tab_accouchement'),
          icon: '🤱',
          hasError: tabsWithErrors.has('accouchement'),
        },
        {
          id: 'grossesse',
          label: t('dossier_tab_grossesse'),
          icon: '🤰',
          hasError: tabsWithErrors.has('grossesse'),
        },
      ];
    }, [t, displayErrors]);

    return (
      <form
        id="patient-form"
        ref={ref}
        onSubmit={handleSubmit}
        className="space-y-6"
        noValidate
      >
        <TabNav tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

        <PartialErrorsBanner errors={sectionErrors} summary={sectionSummary} />

        <div className="pt-1">
          {activeTab === 'core' && (
            <CoreTab
              core={core}
              errors={displayErrors}
              onUpdate={updateCore}
              onAgeValidationChange={handleAgeValidationChange}
            />
          )}
          {activeTab === 'antecedent' && (
            <AntecedentTab
              family={family}
              onToggleFamily={toggleFamily}
              onUpdateFamilyMembers={updateFamilyMembers}
              otherFamily={otherFamily}
              onToggleOtherFamily={toggleOtherFamily}
              onAddOtherFamilyEntry={addOtherFamilyEntry}
              onRemoveOtherFamilyEntry={removeOtherFamilyEntry}
              onUpdateOtherFamilyEntry={updateOtherFamilyEntry}
              historyLists={historyLists}
              onToggleHistoryList={toggleHistoryList}
              onAddHistoryItem={addHistoryItem}
              onUpdateHistoryItem={updateHistoryItem}
              onRemoveHistoryItem={removeHistoryItem}
              disability={disability}
              onToggleDisabilityType={toggleDisabilityType}
              onClearDisability={clearDisability}
              onUpdateDisabilityOther={updateDisabilityOther}
              preventiveCare={preventiveCare}
              onUpdatePreventiveCare={updatePreventiveCare}
            />
          )}
          {activeTab === 'accouchement' && (
            <AccouchementTab
              priorDeliveries={priorDeliveries}
              errors={errors}
              onAdd={addPriorDelivery}
              onRemove={removePriorDelivery}
              onUpdate={updatePriorDelivery}
              onToggleMode={togglePriorDeliveryMode}
              onClearAnomalyError={clearAnomalyError}
            />
          )}
          {activeTab === 'grossesse' && (
            <GrossesseTab
              pregnancy={pregnancy}
              errors={displayErrors}
              onUpdate={updatePregnancy}
              onPatch={patchPregnancy}
              onValidationChange={handlePregnancyValidationChange}
            />
          )}
        </div>
      </form>
    );
  }
);

AddPatientForm.displayName = 'AddPatientForm';

export default AddPatientForm;
